import { useState } from "react";
import { Sparkles, Lock, Plus, Trash2, ArrowRight, Loader2, Mail } from "lucide-react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, PolarRadiusAxis } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingHeader from "@/components/MarketingHeader";

const DIMS = ["subject_line", "hook", "body", "cta", "spam_risk", "personalization"];

// Stable per-row id so dynamic step additions / removals don't trigger
// index-keyed remounts in React.
const newUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

export default function AuditFunnel() {
  const [steps, setSteps] = useState([{ _uid: newUid(), subject: "", body: "" }]);
  const [persona, setPersona] = useState("");
  const [goal, setGoal] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const updateStep = (i, patch) => {
    const next = [...steps];
    next[i] = { ...next[i], ...patch };
    setSteps(next);
  };
  const addStep = () => setSteps([...steps, { _uid: newUid(), subject: "", body: "" }]);
  const rmStep = (i) =>
    setSteps(steps.length > 1 ? steps.filter((_, idx) => idx !== i) : steps);

  const analyze = async () => {
    if (steps.some((s) => !s.subject.trim() || !s.body.trim()))
      return toast.error("Fill subject + body for each step");
    if (!email) return toast.error("Enter your email so we can send the report");
    setBusy(true);
    setReport(null);
    try {
      const r = await api.post("/audit/teaser", {
        steps,
        target_persona: persona,
        goal,
        contact_email: email,
      });
      setReport(r.data);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze");
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    if (!report) return;
    setCheckingOut(true);
    try {
      const r = await api.post("/audit/checkout", {
        audit_id: report.audit_id,
        contact_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <MarketingHeader showAudit={false} />

      <section className="max-w-5xl mx-auto px-6 pt-20 pb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-mono inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Free AI Audit · Sonnet 4.5
        </div>
        <h1 className="font-display text-5xl sm:text-6xl font-semibold tracking-tight mt-5 leading-[1.05]">
          Why your cold emails<br />don't get replies.
        </h1>
        <p className="text-neutral-400 mt-6 text-lg leading-relaxed max-w-2xl">
          Paste your sequence below. Our AI returns scores on six dimensions
          (subject, hook, body, CTA, spam risk, personalization) plus a full
          rewrite of every step - usually in under 30 seconds.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-2 text-xs text-neutral-500 font-mono">
          <span className="px-3 py-1 border border-[#27272a] rounded-full">No signup</span>
          <span className="px-3 py-1 border border-[#27272a] rounded-full">Teaser is free</span>
          <span className="px-3 py-1 border border-[#27272a] rounded-full">Full PDF · $97</span>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            placeholder="Target persona - e.g. Series A SaaS CTOs"
            data-testid="audit-persona"
            className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-md px-3 py-2.5 text-sm"
          />
          <input
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Goal - book a 15-min demo"
            data-testid="audit-goal"
            className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-md px-3 py-2.5 text-sm"
          />
        </div>

        <div className="mt-4 space-y-3">
          {steps.map((s, i) => (
            <div
              key={s._uid}
              className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-2"
              data-testid={`audit-step-${i + 1}`}
            >
              <div className="flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
                <span>Step {i + 1}</span>
                <button
                  onClick={() => rmStep(i)}
                  className="p-1 hover:text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <input
                value={s.subject}
                onChange={(e) => updateStep(i, { subject: e.target.value })}
                placeholder="Subject"
                data-testid={`audit-subject-${i + 1}`}
                className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm"
              />
              <textarea
                value={s.body}
                onChange={(e) => updateStep(i, { body: e.target.value })}
                rows={7}
                data-testid={`audit-body-${i + 1}`}
                placeholder="Email body…"
                className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono leading-relaxed"
              />
            </div>
          ))}
          <button
            onClick={addStep}
            data-testid="audit-add-step"
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#27272a] rounded-md text-sm text-neutral-400 hover:bg-[#0d0d0d]"
          >
            <Plus className="w-4 h-4" strokeWidth={1.8} /> Add step
          </button>
        </div>

        <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 items-stretch">
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" strokeWidth={1.5} />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="audit-email"
              className="w-full bg-[#0d0d0d] border border-[#1a1a1a] rounded-md pl-9 pr-3 py-3 text-sm"
            />
          </div>
          <button
            onClick={analyze}
            disabled={busy}
            data-testid="audit-analyze-button"
            className="flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-md font-medium text-sm hover:bg-neutral-200 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <Sparkles className="w-4 h-4" strokeWidth={1.8} />
            )}
            {busy ? "Analyzing…" : "Run free audit"}
          </button>
        </div>
      </section>

      {report && (
        <section className="max-w-5xl mx-auto px-6 pb-24" data-testid="audit-results">
          <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-xl p-7" style={{ boxShadow: "0 0 40px rgba(255,255,255,0.04)" }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
                  Overall score
                </div>
                <div className="font-display text-6xl font-semibold mt-2 leading-none" data-testid="audit-overall">
                  {report.overall_score}
                  <span className="text-neutral-500 text-2xl">/100</span>
                </div>
              </div>
              <Sparkles className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-neutral-300 mt-5 leading-relaxed text-base">{report.summary}</p>
          </div>

          <div className="mt-4 grid lg:grid-cols-2 gap-3">
            {report.steps.map((sa) => (
              <div
                key={sa.step_number}
                className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5"
                data-testid={`audit-step-result-${sa.step_number}`}
              >
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-3">
                  Step {sa.step_number}
                </div>
                <div className="h-44">
                  <ResponsiveContainer>
                    <RadarChart
                      data={DIMS.map((d) => ({
                        dim: d.replace("_", " "),
                        v: sa.scores[d] || 0,
                      }))}
                    >
                      <PolarGrid stroke="#262626" />
                      <PolarAngleAxis
                        dataKey="dim"
                        tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="v" stroke="#fafafa" fill="#fafafa" fillOpacity={0.15} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-sm text-neutral-300 mt-2">{sa.feedback_teaser}</p>
              </div>
            ))}
          </div>

          <div
            className="mt-6 rounded-xl border border-[#27272a] overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0d0d0d 0%, #161616 100%)" }}
          >
            <div className="p-8 sm:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono inline-flex items-center gap-2">
                  <Lock className="w-3 h-3" strokeWidth={1.8} /> Locked content
                </div>
                <h3 className="font-display text-3xl font-semibold tracking-tight mt-3">
                  Unlock the full report.
                </h3>
                <p className="text-neutral-400 mt-3 text-sm leading-relaxed max-w-md">
                  Get the complete deep-dive PDF - full per-step critique,
                  AI rewrites of every subject + body, and a 14-page audit you
                  can hand to your team or save for reference.
                </p>
                <ul className="mt-5 space-y-1.5 text-sm text-neutral-300">
                  {[
                    "Full per-step rewrites (subject + body)",
                    "Six-dimension breakdown for each step",
                    "Spam-trigger phrases highlighted",
                    "14-page PDF you can download instantly",
                  ].map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="font-display text-5xl font-semibold">
                  ${report.price_usd}
                  <span className="text-neutral-500 text-base ml-1 font-mono">USD</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mt-1">
                  one-time · instant access
                </div>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="audit-checkout-button"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-3 rounded-md font-medium text-sm hover:bg-neutral-200 disabled:opacity-50 w-full sm:w-auto"
                >
                  {checkingOut ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                  ) : (
                    <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                  )}
                  {checkingOut ? "Redirecting…" : "Unlock full report"}
                </button>
                <p className="text-[10px] text-neutral-600 mt-3 font-mono uppercase tracking-[0.18em]">
                  Powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <footer className="max-w-5xl mx-auto px-6 py-8 border-t border-[#1a1a1a] text-xs text-neutral-600 font-mono flex flex-wrap items-center justify-between gap-3">
        <span>OutreachOS · cold email that ships replies</span>
        <span className="flex items-center gap-4 uppercase tracking-[0.2em]">
          <a href="/terms" className="hover:text-white" data-testid="audit-footer-terms">Terms</a>
          <a href="/privacy" className="hover:text-white" data-testid="audit-footer-privacy">Privacy</a>
        </span>
      </footer>
    </div>
  );
}
